import "./Search.css";
import Board from "@publicPortal/components/board/Board";
import ResponsiveAppBar from "@publicPortal/components/responsiveAppBar/ResponsiveAppBar";
import { Tabs, Tab, TextField, MenuItem } from "@mui/material";
import { PlanningAppealsSearch } from "./PlanningAppeal/PlanningAppealsSearch";
import EnforcementNoticeSearch from "./EnforcementNotice/EnforcementNoticeSearch";
import EnforcementAppealsSearch from "./EnforcementAppeal/EnforcementAppealsSearch";
import PlanningApplicationSearch from "./PlanningApplication/PlanningApplicationSearch";
import { WeeklyListsSearch } from "./WeeklyLists/WeeklyListsSearch";
import { atom, useRecoilState } from "recoil";
import { TabNames } from "@publicPortal/utils/enums";
import ErrorBoundary from "@mirage/components/errorBoundary/ErrorBoundary";
import BuildingRegulationsSearch from "./BuildingRegulations/BuildingRegulationsSearch";
import PropertySearch from "./PropertySearch/PropertySearch";
import { useLocation } from "react-router-dom";
import { PPConstants } from "@shared/publicportal/constants";
import useSessionStorage from "@mirage/hooks/useSessionStorage";
import { memo } from "react";
import { useDeviceDetect } from "@mirage/hooks/useDeviceDetect";

const searchTabsState = atom({
    key: "searchTabsState",
    default: TabNames["Property Search"],
});
type ShowTabsMap = {
    [key in keyof typeof TabNames]: boolean;
};
const Search = memo(() => {
    const [currentCard, setCurrentCard] = useRecoilState(searchTabsState);
    const [sessionValue] = useSessionStorage(PPConstants.AppName);
    const { isMobile, isTablet } = useDeviceDetect();
    const location = useLocation();
    const tabs = Object.values(TabNames);
    const showTabsMap: ShowTabsMap = {
        [TabNames["Property Search"]]: true,
        [TabNames["Building Regulations"]]: !!sessionValue.clientSettings?.BUILDING_REGULATIONS,
        [TabNames["Planning Applications"]]: !!sessionValue.clientSettings?.PLANNING_APPLICATIONS,
        [TabNames["Planning Appeals"]]: !!sessionValue.clientSettings?.PLANNING_APPEALS,
        [TabNames["Weekly Lists"]]: !!sessionValue.clientSettings?.WEEKLY_LISTS,
        [TabNames["Enforcement Notices"]]: !!sessionValue.clientSettings?.ENFORCEMENT_NOTICES,
        [TabNames["Enforcement Appeals"]]: !!sessionValue.clientSettings?.ENFORCEMENT_APPEALS,
    };
    const getCardPicker = () => {
        if (isMobile) {
            return getMobileTabPicker();
        }
        return getDefaultTabs();
    };
    return (
        <>
            <ResponsiveAppBar />
            <Board size="big">
                {getCardPicker()}
                <ErrorBoundary key={location.pathname}>
                    <section className="search-form">{getTabContent()}</section>
                </ErrorBoundary>
            </Board>
        </>
    );

    function getTabContent() {
        switch (currentCard) {
            case TabNames["Planning Applications"]:
                return <PlanningApplicationSearch />;
            case TabNames["Planning Appeals"]:
                return <PlanningAppealsSearch />;
            case TabNames["Weekly Lists"]:
                return <WeeklyListsSearch />;
            case TabNames["Enforcement Notices"]:
                return <EnforcementNoticeSearch />;
            case TabNames["Enforcement Appeals"]:
                return <EnforcementAppealsSearch />;
            case TabNames["Building Regulations"]:
                return <BuildingRegulationsSearch />;
            case TabNames["Property Search"]:
                return <PropertySearch />;
            default:
                throw Error(`Wrong Tab Name on getTabContent - ${currentCard}`);
        }
    }

    function getDefaultTabs() {
        const tabsJustify = isTablet || isMobile ? "normal" : "space-evenly";
        return (
            <Tabs
                value={currentCard}
                onChange={(event, newTab: TabNames) => setCurrentCard(newTab)}
                aria-label="nav bar"
                variant="scrollable"
                scrollButtons="auto"
                sx={{ marginBottom: "1rem", "& .MuiTabs-flexContainer": { justifyContent: tabsJustify } }}
            >
                {tabs.map((tabValue: TabNames) => {
                    if (!showTabsMap[tabValue]) {
                        return null;
                    }
                    return <Tab className="searchTab" disableRipple label={tabValue} value={tabValue} key={tabValue} />;
                })}
            </Tabs>
        );
    }

    function getMobileTabPicker() {
        return (
            <TextField
                className="search-by-card-picklist-field"
                id="search-searchType"
                value={currentCard}
                onChange={(event) => setCurrentCard(event.target.value as TabNames)}
                label="Search By"
                variant="outlined"
                size="small"
                select
            >
                {tabs
                    .map((tabValue: TabNames) => {
                        if (!showTabsMap[tabValue]) {
                            return null;
                        }
                        return (
                            <MenuItem key={tabValue} value={tabValue}>
                                {tabValue}
                            </MenuItem>
                        );
                    })
                    .filter(Boolean)}
            </TextField>
        );
    }
});

export default Search;
